import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

export const TABS = [
  { to: "/", label: "Início", id: "home" },
  { to: "/nossa-historia", label: "Nossa História", id: "nossa-historia" },
  { to: "/cerimonia-e-recepcao", label: "Cerimônia & Recepção", id: "cerimonia-e-recepcao" },
  { to: "/padrinhos", label: "Padrinhos", id: "padrinhos" },
  { to: "/lista-de-presentes", label: "Lista de Presentes", id: "lista-de-presentes" },
  { to: "/confirmacao-presenca", label: "Confirmação", id: "confirmacao-presenca" },
];

const Monogram = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center" aria-label="Página inicial">
    <img
      src="/images/monograma.png"
      alt="Monograma de Rodrigo e Talita"
      className="h-11 w-auto object-contain transition-opacity duration-300 group-hover:opacity-75"
    />
  </Link>
);

export default function Nav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-gold-border bg-paper/95 py-2.5 shadow-soft backdrop-blur" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5">
          <Monogram />
          <nav className="hidden items-center gap-6 lg:flex">
            {TABS.map((t) => {
              const active = pathname === t.to;
              return (
                <Link
                  key={t.id}
                  to={t.to}
                  data-testid={`nav-tab-${t.id}`}
                  className={`relative font-sans text-[10.5px] font-medium uppercase tracking-[0.22em] transition-colors ${
                    active ? "text-sage-dark" : "text-ink/70 hover:text-ink"
                  }`}
                >
                  {t.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${active ? "w-full" : "w-0"}`}
                  />
                </Link>
              );
            })}
          </nav>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            aria-label="Abrir menu"
            className="grid h-11 w-11 place-items-center border border-gold-border bg-paper/80 text-ink backdrop-blur lg:hidden"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] flex flex-col bg-paper"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <Monogram />
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                aria-label="Fechar menu"
                className="grid h-11 w-11 place-items-center border border-gold-border text-ink"
              >
                <X size={18} />
              </button>
            </div>
            <img src="/images/floral-side.png" alt="" aria-hidden className="pointer-events-none absolute -right-8 bottom-0 w-36 opacity-70" />
            <nav className="flex flex-1 flex-col items-center justify-center gap-6 px-6">
              {TABS.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={t.to}
                    data-testid={`nav-tab-${t.id}`}
                    className={`font-serif text-3xl transition-colors ${pathname === t.to ? "italic text-sage-dark" : "text-ink hover:text-sage-dark"}`}
                  >
                    {t.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="pb-8 text-center font-serif italic text-gold-deep">09 . 01 . 2027</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
